import { useEffect, useState, useCallback, useRef } from "react";
import api, { formatApiErrorDetail, euro, downloadFile, formatDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import DatePickerField from "@/components/DatePickerField";
import ConfirmDelete from "@/components/ConfirmDelete";
import ImportPreviewDialog from "@/components/ImportPreviewDialog";
import ImportButton from "@/components/ImportButton";
import ColumnFilter from "@/components/ColumnFilter";
import DataTable from "@/components/DataTable";
import { HeadCell } from "@/components/SortLabel";
import { useTableSort } from "@/lib/useTableSort";
import { Plus, Pencil, Trash2, FileCheck2, ReceiptText, FileSpreadsheet, FileText, X } from "lucide-react";
import { toast } from "sonner";

const runDownload = (path, filename) =>
  downloadFile(path, filename).catch(() => toast.error("Errore durante l'esportazione"));

const empty = { numero: "", data: new Date().toISOString().slice(0, 10), importo: "", note: "" };

export default function Benestare() {
  const [commesse, setCommesse] = useState([]);
  const [commessaId, setCommessaId] = useState("");
  const [items, setItems] = useState([]);
  const [kpi, setKpi] = useState({ contabilizzato: 0, riconosciuto: 0 });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  useEffect(() => { api.get("/commesse").then((r) => setCommesse(r.data)); }, []);

  const load = useCallback((cid) => {
    if (!cid) { setItems([]); setKpi({ contabilizzato: 0, riconosciuto: 0 }); return; }
    api.get("/benestare", { params: { commessa_id: cid } }).then((r) => setItems(r.data));
    api.get("/kpi/benestare", { params: { commessa_id: cid } }).then((r) => {
      const row = r.data[0];
      setKpi(row ? { contabilizzato: row.contabilizzato, riconosciuto: row.riconosciuto } : { contabilizzato: 0, riconosciuto: 0 });
    });
  }, []);

  useEffect(() => { load(commessaId); }, [commessaId, load]);

  const openNew = () => { setForm({ ...empty, data: new Date().toISOString().slice(0, 10) }); setEditing(null); setOpen(true); };
  const openEdit = (b) => { setForm({ numero: b.numero, data: b.data, importo: b.importo, note: b.note }); setEditing(b.id); setOpen(true); };

  const importInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/commesse/${commessaId}/benestare/import/preview`, fd);
      setPreviewData(data);
      setPendingFile(file);
      setPreviewOpen(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Anteprima non riuscita");
    } finally {
      setImporting(false);
    }
  };

  const confirmImport = async () => {
    if (!pendingFile) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", pendingFile);
      const { data } = await api.post(`/commesse/${commessaId}/benestare/import`, fd);
      load(commessaId);
      const skip = data.skipped?.length || 0;
      toast.success(`${data.inserted} benestare importati${skip ? ` · ${skip} scartati` : ""}`);
      setPreviewOpen(false);
      setPendingFile(null);
      setPreviewData(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Import non riuscito");
    } finally {
      setImporting(false);
    }
  };

  const save = async () => {
    try {
      const payload = { commessa_id: commessaId, numero: form.numero, data: form.data, importo: parseFloat(form.importo) || 0, note: form.note };
      if (editing) await api.put(`/benestare/${editing}`, payload);
      else await api.post("/benestare", payload);
      toast.success(editing ? "Benestare aggiornato" : "Benestare creato");
      setOpen(false);
      load(commessaId);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => { await api.delete(`/benestare/${id}`); load(commessaId); };

  const pct = kpi.contabilizzato > 0 ? Math.min(100, (kpi.riconosciuto / kpi.contabilizzato) * 100) : 0;

  // ---------- Filtri colonne ----------
  const emptyFilters = { numero: "", mese: [], inserito: [], note: "", importo: "" };
  const [filters, setFilters] = useState(emptyFilters);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const setF = (k, v) => setFilters((prev) => ({ ...prev, [k]: v }));
  const resetFilters = () => { setFilters(emptyFilters); setDateFrom(""); setDateTo(""); };
  useEffect(() => { setFilters(emptyFilters); setDateFrom(""); setDateTo(""); }, [commessaId]); // eslint-disable-line react-hooks/exhaustive-deps

  const meseLabel = (m) => {
    const [y, mm] = m.split("-");
    const nome = new Date(Number(y), Number(mm) - 1, 1).toLocaleDateString("it-IT", { month: "long", year: "numeric" });
    return nome.charAt(0).toUpperCase() + nome.slice(1);
  };
  const mesiOpts = [...new Set(items.map((b) => (b.data || "").slice(0, 7)).filter(Boolean))].sort().reverse().map((m) => ({ value: m, label: meseLabel(m) }));
  const inseritoOpts = [...new Set(items.map((b) => b.created_by).filter(Boolean))].sort().map((v) => ({ value: v, label: v }));
  const has = (val, f) => f === "" || String(val || "").toLowerCase().includes(f.toLowerCase());
  const geq = (val, f) => f === "" || (Number(val) || 0) >= (parseFloat(f) || 0);
  const inSel = (arr, v) => arr.length === 0 || arr.includes(v);

  const filteredItems = items.filter((b) =>
    has(b.numero, filters.numero) && has(b.note, filters.note) &&
    inSel(filters.mese, (b.data || "").slice(0, 7)) &&
    inSel(filters.inserito, b.created_by) &&
    geq(b.importo, filters.importo) &&
    (!dateFrom || (b.data || "") >= dateFrom) &&
    (!dateTo || (b.data || "") <= dateTo)
  );
  const hasActiveFilter = !!dateFrom || !!dateTo || Object.values(filters).some((v) => (Array.isArray(v) ? v.length > 0 : v !== ""));

  const columns = [
    { id: "numero", label: "Numero", width: 160, sortable: true, sortValue: (b) => b.numero || "",
      cell: (b) => <span className="font-semibold text-slate-900">{b.numero || "—"}</span>,
      filter: <ColumnFilter testid="bef-filter-numero" type="text" label="Numero" value={filters.numero} onChange={(v) => setF("numero", v)} /> },
    { id: "data", label: "Data", width: 160, sortable: true, sortValue: (b) => b.data || "",
      cell: (b) => <span className="text-slate-600 tabular-nums">{formatDate(b.data)}</span>,
      filter: <ColumnFilter testid="bef-filter-mese" type="select" label="Data" value={filters.mese} onChange={(v) => setF("mese", v)} options={mesiOpts} /> },
    { id: "note", label: "Note", width: 340, sortable: true, sortValue: (b) => b.note || "",
      cell: (b) => <span className="text-slate-500">{b.note || "—"}</span>,
      filter: <ColumnFilter testid="bef-filter-note" type="text" label="Note" value={filters.note} onChange={(v) => setF("note", v)} /> },
    { id: "importo", label: "Importo", width: 160, align: "right", sortable: true, sortValue: (b) => b.importo || 0,
      cell: (b) => <span className="font-semibold text-emerald-700">{euro(b.importo)}</span>,
      filter: <ColumnFilter testid="bef-filter-importo" type="number" label="Importo" value={filters.importo} onChange={(v) => setF("importo", v)} align="end" /> },
    { id: "inserito", label: "Inserito da", width: 150, sortable: true, sortValue: (b) => b.created_by || "",
      cell: (b) => <span className="text-slate-500 text-sm">{b.created_by || "—"}</span>,
      filter: <ColumnFilter testid="bef-filter-inserito" type="select" label="Inserito da" value={filters.inserito} onChange={(v) => setF("inserito", v)} options={inseritoOpts} /> },
    { id: "azioni", label: "Azioni", width: 120, align: "right", sortable: false,
      cell: (b) => (
        <div className="flex items-center justify-end gap-0.5">
          <Button variant="ghost" size="icon" data-testid={`edit-bef-${b.id}`} onClick={() => openEdit(b)} className="h-8 w-8 text-slate-500 hover:text-blue-700"><Pencil className="h-4 w-4" /></Button>
          <ConfirmDelete title="Eliminare benestare?" description="Il documento verrà rimosso." actionTestid={`confirm-delete-bef-${b.id}`} onConfirm={() => remove(b.id)}>
            <Button variant="ghost" size="icon" data-testid={`delete-bef-${b.id}`} className="h-8 w-8 text-slate-500 hover:text-rose-600"><Trash2 className="h-4 w-4" /></Button>
          </ConfirmDelete>
        </div>) },
  ];

  return (
    <>
      

      <ImportPreviewDialog
        open={previewOpen} onOpenChange={setPreviewOpen}
        title="Anteprima import benestare" preview={previewData}
        onConfirm={confirmImport} importing={importing}
        columns={[
          { key: "numero", label: "Numero" },
          { key: "data", label: "Data" },
          { key: "importo", label: "Importo €", right: true, format: (v) => euro(v) },
          { key: "note", label: "Note" },
        ]}
      />

      <div className="flex-1 overflow-auto px-6 py-6 lg:px-8 space-y-5">
        <PageHeader title="Benestare Emissione Fattura" subtitle="Importi riconosciuti dal cliente sull'avanzamento lavori">
        {commessaId && (
          <>
            <input ref={importInputRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleImportFile} className="hidden" data-testid="import-benestare-input" />
            <ImportButton testid="import-benestare" importing={importing}
              onPickFile={() => importInputRef.current?.click()}
              onTemplate={() => runDownload(`/commesse/${commessaId}/benestare/template`, "template_benestare.xlsx")} />
            <Button variant="outline" size="sm" data-testid="bef-export-excel"
              onClick={() => runDownload(`/commesse/${commessaId}/benestare/export/excel`, `benestare_${commessaId}.xlsx`)}>
              <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Excel
            </Button>
            <Button variant="outline" size="sm" data-testid="bef-export-pdf"
              onClick={() => runDownload(`/commesse/${commessaId}/benestare/export/pdf`, `benestare_${commessaId}.pdf`)}>
              <FileText className="h-4 w-4 mr-1.5" /> PDF
            </Button>
            <Button data-testid="add-benestare-button" onClick={openNew} className="bg-blue-700 hover:bg-blue-800 active:scale-[0.98] transition-transform">
              <Plus className="h-4 w-4 mr-1.5" /> Nuovo BEF
            </Button>
          </>
        )}
      </PageHeader>
        <div className="flex items-end gap-3">
          <div className="w-80">
            <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Commessa</Label>
            <Select value={commessaId} onValueChange={setCommessaId}>
              <SelectTrigger data-testid="bef-select-commessa" className="mt-1.5 bg-white"><SelectValue placeholder="Seleziona commessa" /></SelectTrigger>
              <SelectContent>{commesse.map((c) => <SelectItem key={c.id} value={c.id}>{c.codice} · {c.nome}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {commessaId && (
            <>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Data da</Label>
                <div className="mt-1.5"><DatePickerField testid="bef-filter-data-da" value={dateFrom} onChange={setDateFrom} placeholder="Da…" /></div>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Data a</Label>
                <div className="mt-1.5"><DatePickerField testid="bef-filter-data-a" value={dateTo} onChange={setDateTo} placeholder="A…" /></div>
              </div>
            </>
          )}
          {commessaId && hasActiveFilter && (
            <Button variant="outline" size="sm" data-testid="bef-clear-filters" onClick={resetFilters} className="h-10 gap-1.5 text-slate-500">
              <X className="h-4 w-4" /> Azzera filtri
            </Button>
          )}
        </div>

        {!commessaId ? (
          <EmptyState title="Seleziona una commessa" text="Scegli una commessa per gestire i Benestare e vedere il confronto con il contabilizzato." />
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              <StatCard label="Contabilizzato" value={euro(kpi.contabilizzato)} tint="text-blue-700" testid="bef-contabilizzato" />
              <StatCard label="Riconosciuto (BEF)" value={euro(kpi.riconosciuto)} tint="text-emerald-600" testid="bef-riconosciuto" />
              <Card className="p-5 rounded-md border-slate-200 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">Avanzamento riconosciuto</span>
                  <span className="text-sm font-bold text-slate-900 tabular-nums">{pct.toFixed(0)}%</span>
                </div>
                <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
                </div>
                <p className="text-xs text-slate-400 mt-2">Differenza: {euro(kpi.contabilizzato - kpi.riconosciuto)}</p>
              </Card>
            </div>

            <Card className="rounded-md border-slate-200 shadow-sm overflow-hidden">
              {items.length === 0 ? (
                <div className="py-16 flex flex-col items-center text-center">
                  <div className="h-14 w-14 rounded-lg bg-slate-100 flex items-center justify-center mb-4"><FileCheck2 className="h-6 w-6 text-slate-400" /></div>
                  <p className="text-slate-600 font-medium">Nessun benestare</p>
                  <p className="text-sm text-slate-400 mt-1 mb-4">Registra il primo documento di benestare del cliente.</p>
                  <Button variant="outline" onClick={openNew}><Plus className="h-4 w-4 mr-1.5" /> Nuovo BEF</Button>
                </div>
              ) : (
                <DataTable tableKey="benestare" columns={columns} rows={filteredItems} rowKey={(b) => b.id}
                  rowTestId={(b) => `bef-row-${b.id}`} containerClassName="max-h-[calc(100vh-360px)]"
                  initialSort={{ key: "data", dir: "desc" }} />
              )}
            </Card>
          </>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Modifica benestare" : "Nuovo benestare"}</DialogTitle>
            <DialogDescription>Importo riconosciuto dal cliente per la commessa selezionata.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-slate-700 text-sm">Numero documento</Label><Input data-testid="bef-numero-input" value={form.numero} onChange={(e) => setForm({ ...form, numero: e.target.value })} className="mt-1.5" placeholder="BEF-001" /></div>
              <div><Label className="text-slate-700 text-sm">Data</Label><div className="mt-1.5"><DatePickerField testid="bef-data-input" value={form.data} onChange={(v) => setForm({ ...form, data: v })} /></div></div>
            </div>
            <div><Label className="text-slate-700 text-sm">Importo riconosciuto €</Label><Input data-testid="bef-importo-input" type="number" step="0.01" value={form.importo} onChange={(e) => setForm({ ...form, importo: e.target.value })} className="mt-1.5" /></div>
            <div><Label className="text-slate-700 text-sm">Note</Label><Input data-testid="bef-note-input" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1.5" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button data-testid="save-bef-button" onClick={save} className="bg-blue-700 hover:bg-blue-800">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

const StatCard = ({ label, value, tint, testid }) => (
  <Card className="p-5 rounded-md border-slate-200 shadow-sm">
    <div className="flex items-center justify-between">
      <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">{label}</span>
      <ReceiptText className={`h-[18px] w-[18px] ${tint}`} />
    </div>
    <div data-testid={testid} className={`mt-3 font-heading text-2xl font-bold tabular-nums tracking-tight ${tint}`}>{value}</div>
  </Card>
);

const EmptyState = ({ title, text }) => (
  <Card className="rounded-md border-slate-200 shadow-sm py-20 flex flex-col items-center text-center">
    <div className="h-14 w-14 rounded-lg bg-slate-100 flex items-center justify-center mb-4"><FileCheck2 className="h-6 w-6 text-slate-400" /></div>
    <p className="text-slate-600 font-medium">{title}</p>
    <p className="text-sm text-slate-400 mt-1 max-w-xs">{text}</p>
  </Card>
);
